import React, {
  useState,
  useMemo,
  useEffect,
  Fragment,
  useRef,
  lazy,
  Suspense,
} from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import validator from "validator";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactToPrint from "react-to-print";

// React-Bootstrap
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Modal,
  Spinner,
  Badge,
  InputGroup,
  Table,
} from "react-bootstrap";

// Custom API and utility imports
import NavLeft from "../CRM/NavLeft";
import PrintDemography from "./customerForm/printDemography";
import {
  getCustomerCareList,
  getDoctors,
  getCustomers,
  getPrescriptions,
  getCountries,
  getAppointments,
  deleteAppointmentHandler,
  addAppointment,
} from "../../../Data/Customer/api";
import {
  findNearestStartTime,
  formatTime12Hour,
  generateTimeOptions,
} from "../CRM/OT/utils/appointmentValidation";
import { calculateAge } from "./utils/age";
import "../CRM/styles/style.css";
import FileUpload from "../../SubComponents/FileUpload";
import ProfilePicture from "./ProfilePicture";

// Lazy Loaded Component
const PatientHistory = lazy(() => import("./PatientHistory"));

const CrmCLT = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authData = location.state;

  const branch = sessionStorage.getItem("branch")
    ? JSON.parse(sessionStorage.getItem("branch"))
    : "";

  // File Upload State
  const [imageUrl, setImageUrl] = useState("");
  const [inputKey, setInputKey] = useState(Date.now());

  // --- Initial Form State Object ---
  const initialFormState = {
    fname: "",
    sname: "",
    dob: "",
    contactno: "",
    address: "",
    nationality: "",
    locality: branch === "CLT" ? "Calicut" : "",
    treatmenttype: "PRP",
    remarks: "",
    patientcoordinator: "",
    bloodGroup: "",
    age: "",
    source: "",
    createdBy: "",
    reasonforVisit: "",
    dnd: "",
    sex: "male",
    emailId: "",
    occupation: "",
    pastPrescription: "",
    reactionAlergies: "",
    doctorName: "",
    visitType: "",
    advancePayment: "",
    advancePaymentDate: "",
    togglePastPrescript: false,
    toggleAllergy: false,
    sourceDetails: "",
    patientSession: "",
    patientState: "",
    district: "",
    category: "",
    photoKey: "",
    fileKey: "", // Single file key string for form fill-up attachments
  };

  // --- Consolidated State ---
  const [formData, setFormData] = useState(initialFormState);

  // Derive photo preview URL from loaded/saved form data or upload state
  const photoPreviewUrl = useMemo(() => {
    console.log("Calculating photoPreviewUrl with formData:", formData, "and imageUrl:", imageUrl);
    const key = formData.photoKey || formData.fileKey || imageUrl;
    if (!key) return "";
    if (key.startsWith("http://") || key.startsWith("https://") || key.startsWith("data:")) {
      return key;
    }
    return `${process.env.REACT_APP_S3_URL || ""}${key}`;
  }, [formData.photoKey, formData.fileKey, imageUrl]);

  // --- Auth & Session State ---
  const [token] = useState(
    authData ? authData.token : JSON.parse(sessionStorage.getItem("token"))
  );
  const [user] = useState(JSON.parse(sessionStorage.getItem("username")));
  const [rights] = useState(JSON.parse(sessionStorage.getItem("rights")));

  // --- MRN & Workflow Controls ---
  const [MRN, setMRN] = useState("");
  const [tempMRN, setTempMRN] = useState("");
  const [verified, setVerified] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchData, setSearchData] = useState("");
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [customerData, setCustomerData] = useState("");
  const [appointmentData, setAppointmentData] = useState("");
  const [modalName, setModalName] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [otData, setOTData] = useState("");
  const [isEmailValid, setEmailValid] = useState(true);
  const [isMobileValid, setIsMobileValid] = useState(true);
  const [editSavedData, setEditSavedData] = useState("");
  const [doctorsList, setDoctorsList] = useState([]);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [prescriptionHistory, setPrescriptionHistory] = useState([]);
  const [isLoadingAppointment, setIsLoadingAppointment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [tempPatientData, setTempPatientData] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const componentRef = useRef();
  const currentDateIST = new Date();
  const [datewithoutHours, setDatewithoutHours] = useState(
    new Date(currentDateIST.setHours(0, 0, 0, 0))
  );

  const toggle = () => setModal(!modal);

  // --- Static Option Constants ---
  const sourceOptions = [
    "Reference",
    "Hoardings",
    "Advertisement",
    "Word of mouth",
    "Through a friend/relative",
    "Instagram",
    "Youtube",
    "Facebook",
    "Twitter",
    "Whatsapp",
    "Google",
    "X",
  ];

  const treatmentTypeOptions = [
    "PRP SESSION WISE",
    "PRP 3 SESSION PKG",
    "PRP 6 SESSION PKG",
    "PRP PKG",
    "GFC SESSION WISE",
    "GFC 3 SESSION PKG",
    "GFC 6 SESSION PKG",
    "GFC PKG",
    "HAIR TRANSPLANT",
    "HAIR TRANSPLANT FRONTAL AREA",
    "HAIR TRANSPLANTATION 2000 GRAFTS",
    "HAIR TRANSPLANTATION 2500 GRAFTS",
    "HAIR TRANSPLANTATION 3000 GRAFTS",
    "HAIR TRANSPLANTATION 3500 GRAFTS",
    "HAIR TRANSPLANTATION 4000 GRAFTS",
    "HAIR TRANSPLANTATION 4000 MAXIMUM COVERAGE",
    "DERMAT CONSULTATION",
    "HAIRLOSS CONSULTATION",
    "PRP",
    "GFC",
    "BIOTIN PRP",
  ];

  const occupationOptions = [
    "Self Employed",
    "Home Maker",
    "Student",
    "Professional",
  ];

  // --- Utility Functions ---
  const titleCaseHandler = (name) => {
    return name
      .split(" ")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const formatDate = (dte) => {
    const fdate = new Date(dte);
    const ISTOffset = 330;
    const ISTTime = new Date(fdate.getTime() + ISTOffset * 60000);
    return ISTTime.toISOString().split("T")[0];
  };

  const validateDate = (dateString) => {
    if (typeof dateString !== "string") return false;
    const regex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = dateString.match(regex);
    if (!match) return false;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);

    const date = new Date(year, month, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    );
  };

  // --- Universal Input Handler ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;

    if (MRN !== "") setSaveSuccess(false);

    setFormData((prev) => {
      const updated = { ...prev, [name]: val };
      if (name === "dob" && val) {
        updated.age = calculateAge(val).toString();
      }
      return updated;
    });
  };

  // --- Populate State from API/Search Data ---
  const updateSrchData = (data) => {
    if (!data) return;
    setCustomerData(data);
    setMRN(data.mrn || "");

    setFormData({
      fname: data.fname || "",
      sname: data.sname || "",
      contactno: data.contactno || "",
      emailId: data.emailId || data.emailid || "",
      address: data.address || "",
      nationality: data.nationality || data.country || "",
      locality: data.location || (branch === "CLT" ? "Calicut" : ""),
      treatmenttype: data.treatmenttype || "PRP",
      occupation: data.occupation || "",
      pastPrescription: data.pastPrescription || "",
      reactionAlergies: data.reactionAlergies || "",
      remarks: data.remarks || "",
      patientcoordinator: data.patientcoordinator || "",
      dob: data.dob || "",
      sex: data.sex || "male",
      doctorName: data.doctorName || data.doctor || "",
      visitType: data?.additional?.visitType || "",
      advancePayment: data?.additional?.advancePayment || "",
      advancePaymentDate: data?.additional?.advancePaymentDate || "",
      bloodGroup: data?.additional?.bloodGroup || "",
      age: data.age || "",
      source: data.source || "",
      createdBy: data.createdBy || "",
      reasonforVisit: data.reasonforVisit || "",
      dnd: data.dnd || "",
      togglePastPrescript: !!data.togglePastPrescript,
      toggleAllergy: !!data.toggleAllergy,
      sourceDetails: data.sourceDetails || "",
      patientSession: data.patientSession || "",
      patientState: data.patientState || data.state || "",
      district: data.district || "",
      category: data.category || "",
      photoKey: data.photoKey || "",
      fileKey: data.fileKey || "",
    });
  };

  const searchUpdateHandler = (data) => {
    if (data) updateSrchData(data);
  };

  const customerDataHandler = (data) => {
    if (Array.isArray(data)) setCustomerHistory(data);
  };

  // --- Effects ---
  useEffect(() => {
    if (formData.contactno.length > 9) {
      const contactIsValid = validator.isMobilePhone(formData.contactno);
      setIsMobileValid(contactIsValid);
      if (contactIsValid) {
        getCustomerCareList(token, branch, formData.contactno, "", navigate).then(
          (res) => {
            setCustomerHistory(res || []);
            if (res && res.length > 0) {
              if (!searchData && !sessionStorage.getItem("srchData")) {
                setModalName("Customer History");
                toggle();
              }
            }
          }
        );
      }
    }
  }, [formData.contactno, token, branch, navigate, searchData]);

  useEffect(() => {
    if (!countryList || countryList.length === 0) {
      const fetchCountries = async (retryCount = 3) => {
        try {
          const res = await getCountries(token, "", navigate);
          setCountryList(res || []);
        } catch (err) {
          console.error("Error fetching countries:", err);
          if (retryCount > 0) {
            setTimeout(() => fetchCountries(retryCount - 1), 1000);
          }
        }
      };
      fetchCountries();
    }
  }, [token, navigate, countryList]);

  useEffect(() => {
    const fetchDoctorData = async () => {
      if (branch && token) {
        try {
          const res = await getDoctors(token, branch, navigate);
          setDoctorsList(res || []);
          localStorage.setItem("doctorsData", JSON.stringify(res));
        } catch (error) {
          console.error(error.message);
        }
      }
    };
    if (!token) navigate("/");
    else fetchDoctorData();
  }, [branch, token, navigate]);

  useEffect(() => {
    if (token) {
      const nDate = new Date();
      const withoutHours = new Date(nDate.setHours(0, 0, 0, 0));
      getAppointments(token, withoutHours, branch, "", "", "", navigate)
        .then((res) => {
          setIsLoadingAppointment(false);
          setAppointmentData(res);
        })
        .catch(() => setIsLoadingAppointment(false));
    }
  }, [authData, MRN, branch, token, navigate]);

  useEffect(() => {
    if (validateDate(selectedDate)) {
      const nDate = new Date(selectedDate);
      setDatewithoutHours(new Date(nDate.setHours(0, 0, 0, 0)));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (searchData && searchData.date) {
      setSelectedDate(formatDate(searchData.date));
    } else {
      setSelectedDate(new Date().toISOString().split("T")[0]);
    }
  }, [searchData]);

  useEffect(() => {
    if (verified) {
      submitHandler();
      setVerified(false);
    }
  }, [verified]);

  useEffect(() => {
    const localSrch = sessionStorage.getItem("srchData");
    if (localSrch) {
      try {
        const parsed = JSON.parse(localSrch);
        if (parsed && !editMode) updateSrchData(parsed);
      } catch (err) {
        console.error("Invalid session search data", err);
      }
    }
  }, [editMode]);

  // --- Memoized API Payloads ---
  const postAppointmentData = useMemo(
    () => ({
      mrn: MRN,
      date: tempPatientData.appointmentdate,
      time: tempPatientData.time,
      endTime: tempPatientData.endTime,
      doctor: tempPatientData.doctor,
      createdBy: user,
      fname: formData.fname,
      sname: formData.sname,
      contactno: formData.contactno,
      department: tempPatientData.department,
      visitType: tempPatientData.visitType,
      visitReason: tempPatientData.visitReason,
    }),
    [MRN, tempPatientData, user, formData.fname, formData.sname, formData.contactno]
  );

  const additional = useMemo(
    () => ({
      visitType: formData.visitType,
      advancePayment: formData.advancePayment,
      advancePaymentDate: formData.advancePaymentDate,
      bloodGroup: formData.bloodGroup,
    }),
    [formData.visitType, formData.advancePayment, formData.advancePaymentDate, formData.bloodGroup]
  );

  const postData = useMemo(
    () => ({
      ...formData,
      location: formData.locality,
      date: datewithoutHours,
      createdBy: user,
      additional,
    }),
    [formData, datewithoutHours, user, additional]
  );

  const editPostData = useMemo(
    () => ({
      ...postData,
      mrn: MRN,
      createdBy: formData.createdBy || user,
    }),
    [postData, MRN, formData.createdBy, user]
  );

  // --- Handlers ---
  const handleAuthError = (err) => {
    if (
      err.response?.data?.err?.name === "TokenExpiredError" ||
      err.response?.data?.message === "Unauthorized"
    ) {
      alert("Session expired. Please log in again.");
      sessionStorage.clear();
      navigate("/cri-crm");
    }
  };

  const editPostHandler = async () => {
    if (saveSuccess) {
      alert("Already Saved");
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_CRI_API}edit-customer`,
        editPostData,
        {
          params: { branch, mrn: MRN },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        alert("Edit successful");
        setSaveSuccess(true);
        getCustomers(token, branch, MRN, formData.doctorName, datewithoutHours, navigate).then(
          (res) => {
            if (res && res[0]) {
              updateSrchData(res[0]);
              sessionStorage.setItem("srchData", JSON.stringify(res[0]));
              setEditSavedData(res[0]);
            }
          }
        );
      }
    } catch (err) {
      handleAuthError(err);
    }
  };

  const submitHandler = async () => {
    if (MRN && editMode) {
      editPostHandler();
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_CRI_API}add-customer`,
        postData,
        {
          params: { branch },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 200) {
        setLoading(false);
        setSaveSuccess(true);
        alert("Save successful");
        setMRN(response.data.mrn);
        sessionStorage.setItem("srchData", JSON.stringify(response.data));

        if (tempMRN) {
          deleteAppointmentHandler(
            token,
            branch,
            datewithoutHours,
            "",
            tempMRN,
            navigate
          );
          if (response.data.mrn) {
            addAppointment(
              token,
              branch,
              { ...postAppointmentData, mrn: response.data.mrn },
              navigate
            );
          }
        }
      }
    } catch (err) {
      setLoading(false);
      handleAuthError(err);
    }
  };

  const basicFormValidation = () => {
    if (formData.fname.trim() === "") {
      alert("Kindly enter Patient Name");
      return false;
    }
    if (formData.contactno.trim() === "" || formData.contactno.length < 10) {
      alert("Kindly enter a valid 10-digit mobile number");
      return false;
    }
    if (!isMobileValid) {
      alert("Kindly enter a valid mobile number");
      return false;
    }
    return true;
  };

  const validate = () => {
    if (branch === "CLT") {
      if (!basicFormValidation()) return false;
      if (formData.dob.trim() === "" && formData.age.trim() === "") {
        alert("Kindly enter date of birth or age");
        return false;
      }
      if (!additional.visitType) {
        alert("Kindly select Visit Type");
        return false;
      }
      if (formData.address.trim() === "") {
        alert("Kindly enter address");
        return false;
      }
      if (!formData.treatmenttype) {
        alert("Kindly enter treatment type");
        return false;
      }
      return true;
    }
    return basicFormValidation();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) setVerified(true);
    else {
      setErrorMessage("Missing data, kindly complete required fields.");
      setModalName("error");
      toggle();
    }
  };

  const clearForm = () => {
    setSelectedDate(formatDate(new Date()));
    setFormData(initialFormState);
    setImageUrl("");
    setInputKey(Date.now());
    setMRN("");
    setVerified(false);
    setErrorMessage("");
    setSearchData("");
    setModal(false);
    setEditMode(false);
    setCustomerData("");
    setAppointmentData("");
    setModalName("");
    setSaveSuccess(false);
    sessionStorage.removeItem("srchData");
  };

  const handleInputMouseDown = (e) => {
    if (MRN !== "" && !editMode) e.preventDefault();
  };

  return (
    <div className="crm-layout d-flex w-100 min-vh-100">
      <NavLeft
        searchUpdateHandler={searchUpdateHandler}
        customerDataHandler={customerDataHandler}
        customerData={customerData}
        doctorsList={doctorsList}
        token={token}
        mrn={MRN}
        branch={branch}
        rights={rights}
        contactno={formData.contactno}
      />

      <Container fluid className="p-4 content-workspace flex-grow-1">
        {/* Header Bar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white shadow-sm rounded-4"
        >
          <div>
            <h4 className="mb-1 text-primary fw-bold">Customer Management Hub</h4>
            <div className="text-muted small">
              Branch:{" "}
              <Badge bg="secondary" className="px-2 py-1">
                {branch || "N/A"}
              </Badge>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setModalName("Temp ID Customers List");
                toggle();
              }}
            >
              Temp Appts List
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={clearForm}>
              Clear Workspace
            </Button>
            {MRN && (
              <Button
                variant={editMode ? "success" : "warning"}
                size="sm"
                onClick={() => setEditMode(!editMode)}
              >
                {editMode ? "Lock Editing" : "Modify Patient"}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Patient Intake Form */}
        <Row>
          <Col lg={12}>
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4">
              <Card.Header className="bg-primary text-white p-3 border-0 d-flex justify-content-between align-items-center">
  <div className="d-flex align-items-center gap-3">
    {/* Profile Picture Render */}
    <ProfilePicture profileFileKey={formData.photoKey || formData.fileKey || imageUrl} />
  
    <div>
      <h5 className="mb-0 text-white fw-semibold">
        {formData.fname ? `${formData.fname} ${formData.sname}` : "Patient Demographics & Clinical Intake"}
      </h5>
      {formData.contactno && (
        <small className="text-white-50">{formData.contactno}</small>
      )}
    </div>
  </div>

  {MRN && (
    <Badge bg="light" text="dark" className="fs-6 fw-normal">
      MRN: {MRN}
    </Badge>
  )}
</Card.Header>

                <Card.Body className="p-4 bg-light-gradient">
                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">First Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="fname"
                            placeholder="First Name"
                            value={formData.fname}
                            onMouseDown={handleInputMouseDown}
                            onChange={(e) => {
                              e.target.value = titleCaseHandler(e.target.value);
                              handleChange(e);
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Surname</Form.Label>
                          <Form.Control
                            type="text"
                            name="sname"
                            placeholder="Surname"
                            value={formData.sname}
                            onMouseDown={handleInputMouseDown}
                            onChange={(e) => {
                              e.target.value = titleCaseHandler(e.target.value);
                              handleChange(e);
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Contact Number *</Form.Label>
                          <Form.Control
                            type="tel"
                            name="contactno"
                            placeholder="Contact Number"
                            value={formData.contactno}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Email Address</Form.Label>
                          <Form.Control
                            type="email"
                            name="emailId"
                            placeholder="Email Address"
                            value={formData.emailId}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Date of Birth</Form.Label>
                          <Form.Control
                            type="date"
                            name="dob"
                            value={formData.dob ? formData.dob.split("T")[0] : ""}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={1}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Age</Form.Label>
                          <Form.Control
                            type="number"
                            name="age"
                            placeholder="Age"
                            value={formData.age}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Gender</Form.Label>
                          <Form.Select
                            name="sex"
                            value={formData.sex}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          >
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Locality / Branch</Form.Label>
                          <Form.Control
                            type="text"
                            name="locality"
                            value={formData.locality}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Nationality</Form.Label>
                          <Form.Select
                            name="nationality"
                            value={formData.nationality}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          >
                            <option value="">Select Country</option>
                            {countryList.map((c, i) => (
                              <option key={i} value={c.name}>
                                {c.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={1}
                            name="address"
                            placeholder="Full Address"
                            value={formData.address}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Doctor</Form.Label>
                          <Form.Select
                            name="doctorName"
                            value={formData.doctorName}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          >
                            <option value="">Choose Practitioner</option>
                            {doctorsList.map((doc, idx) => (
                              <option key={idx} value={doc.doctorName}>
                                {doc.doctorName}
                              </option>
                            ))}----
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Treatment Modality</Form.Label>
                          <Form.Select
                            name="treatmenttype"
                            value={formData.treatmenttype}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          >
                            {treatmentTypeOptions.map((opt, key) => (
                              <option key={key} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Visit Classification</Form.Label>
                          <Form.Select
                            name="visitType"
                            value={formData.visitType}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          >
                            <option value="">Select Visit Variant</option>
                            <option value="Consultation">Consultation</option>
                            <option value="Procedure">Procedure</option>
                            <option value="Follow-up">Follow-up</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Lead Source</Form.Label>
                          <Form.Select
                            name="source"
                            value={formData.source}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          >
                            <option value="">Select Channel Source</option>
                            {sourceOptions.map((src, idx) => (
                              <option key={idx} value={src}>
                                {src}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Clinical Narrative / Intake Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            name="remarks"
                            placeholder="Clinical Remarks / Intakes"
                            value={formData.remarks}
                            onMouseDown={handleInputMouseDown}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Form Document / ID Attachment</Form.Label>
                          <FileUpload
                            mrn={MRN}
                            mode="multiple"
                            formData={formData}
                            setFormData={setFormData}
                            inputKey={inputKey}
                            imageUrl={imageUrl}
                            setImageUrl={setImageUrl}
                            bra
                          />
                       
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Action Bar */}
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <Button variant="secondary" onClick={clearForm}>
                        Reset Form
                      </Button>
                      <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? (
                          <Spinner animation="border" size="sm" />
                        ) : editMode ? (
                          "Update Patient Data"
                        ) : (
                          "Save Patient"
                        )}
                      </Button>
                        <Button color="primary" size="sm" onClick={() => navigate("/id-card", {
      state: {
        customer: formData, // Matches location.state.customer in your IDCard component
      },
    })}>
      View / Print ID Card
    </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CrmCLT;